import React, { useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { orderPurchase } from '../../../../slices/orders.slice';
import { toast } from 'react-toastify';
import PriceBreakdown from '../../../../components/OrderModal/PriceBreakdown';
import OrderModalContent from '../../../../components/OrderModal/OrderModalContent';
import { getSitePrice } from '../../../../utils/credits';

const OrderButton = ({ order, version }) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [screeningDate, setScreeningDate] = useState(null);
    const { user } = useSelector(state => state.auth);

    if (!order) return null;

    const { statusWorkflow } = order;

    // Determine if this is a Movie on Request title
    const isMovieOnRequest = order?.version?.isMovieOnRequest || order.status === 'movie-on-request';
    const actionWord = isMovieOnRequest ? 'Request' : 'Order';
    const actionWordUpper = actionWord.toUpperCase();

    // Calculate minimum hours based on movie type
    const getMinimumHours = () => {
        if (isMovieOnRequest) return 72; // Movie on Request: 3 days
        if (statusWorkflow === 'ready-to-play' || statusWorkflow === 'ready-to-order' || statusWorkflow === 'ready-on-mediablock') return 0; // Ready to Play: immediate
        return 6; // Default: Key on Request
    };

    const getMinimumHoursLabel = () => {
        if (isMovieOnRequest) {
            return 'Movie on Request requires 72 hours (3 days) notice from now';
        }
        if (statusWorkflow === 'ready-to-play' || statusWorkflow === 'ready-to-order' || statusWorkflow === 'ready-on-mediablock') {
            return 'Ready to Play titles are available immediately';
        }
        return null;
    };

    const minimumHours = getMinimumHours();
    const minimumHoursLabel = getMinimumHoursLabel();

    const toggleModal = () => {
        if (!isOpen) {
            const earliest = new Date();
            earliest.setSeconds(0, 0);
            earliest.setHours(earliest.getHours() + minimumHours);
            setScreeningDate(earliest);
        }
        setIsOpen(!isOpen);
    };

    const isScreeningDateValid = () => {
        if (!screeningDate) return false;
        if (minimumHours === 0) return true;
        const minimumDate = new Date();
        minimumDate.setHours(minimumDate.getHours() + minimumHours);
        return Math.floor(screeningDate.getTime() / 60000) >= Math.floor(minimumDate.getTime() / 60000);
    };

    const handleOrder = async () => {
        const body = { screeningDate };

        try {
            const promise = dispatch(orderPurchase({ orderId: order._id, body })).unwrap();
            void toast.promise(promise, {
                pending: 'Processing order',
                success: 'Order processed successfully',
                error: { render: ({ data }) => data || 'Error creating the order' }
            });
            await promise;
            setIsOpen(false);
        } catch {
            // Error already shown by toast
        }
    };

    const calculatedPrice = screeningDate
        ? getSitePrice(version, user.parentOrganization, screeningDate, 'number')
        : null;
    const hasEnoughCredits = calculatedPrice !== null &&
        (user.parentOrganization.creditAmount - calculatedPrice) >= 0;

    return (
        <>
            <Button color="primary" outline onClick={toggleModal}>
                <span className="fas fa-shopping-cart me-2" />
                {actionWordUpper} NOW
            </Button>

            <Modal isOpen={isOpen} toggle={toggleModal} size="lg" fullscreen="lg" scrollable>
                <ModalHeader toggle={toggleModal} tag="div">
                    <div className="text-expand text-uppercase mb-2">Create {actionWord.toLowerCase()}</div>
                    <h3 className="text-uppercase text-primary">Create {actionWord.toLowerCase()}</h3>
                </ModalHeader>
                <ModalBody>
                    <OrderModalContent
                        modalType="ORDER"
                        order={order}
                        user={user}
                        screeningDate={screeningDate}
                        setScreeningDate={setScreeningDate}
                        minimumHours={minimumHours}
                        minimumHoursLabel={minimumHoursLabel}
                    />
                </ModalBody>
                <ModalFooter>
                    <div className="d-flex justify-content-between align-items-baseline w-100 gap-4">
                        <PriceBreakdown user={user} amountToSubtract={calculatedPrice} />
                        <div>
                            <Button
                                color="primary"
                                outline
                                onClick={handleOrder}
                                disabled={!hasEnoughCredits || !isScreeningDateValid()}
                            >
                                {actionWord} now
                            </Button>
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default OrderButton;
